/**
 * マウスでのノードのドラッグ移動と、ファイルのドロップ添付。
 *
 * 行の上寄り・下寄り・中央のどこで離したかで、
 * 「前の兄弟」「次の兄弟」「子」のどれにするかを決める。
 * ノードを掴んでいないドラッグはファイル添付として扱う。
 *
 * タッチ操作の並べ替えは別経路（長押しドラッグ）で、ここには含まれない。
 */
export function initNodeDragDrop({
  outlineEl,
  state,
  t,
  setStatus,
  apiUpload,
  flushNodeText,
  renderTree,
  moveNode,
  getChildren,
  isDescendant,
}) {
  let draggingNodeId = null;

  outlineEl.addEventListener('dragstart', (event) => {
    if (state.readonly) {
      event.preventDefault();
      return;
    }
    const toggle = event.target.closest('.toggle-button');
    if (!toggle) {
      event.preventDefault();
      return;
    }
    const row = toggle.closest('.node');
    if (!row) {
      event.preventDefault();
      return;
    }
    draggingNodeId = Number(row.dataset.id);
    row.classList.add('is-dragging');
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(draggingNodeId));
  });

  outlineEl.addEventListener('dragend', (event) => {
    const row = event.target.closest('.node');
    if (row) {
      row.classList.remove('is-dragging');
    }
    clearDragOverStyles();
    draggingNodeId = null;
  });

  function clearDragOverStyles() {
    for (const el of outlineEl.querySelectorAll('.node')) {
      el.classList.remove('drag-over-above', 'drag-over-below', 'drag-over-inside');
    }
  }

  outlineEl.addEventListener('dragover', (event) => {
    if (state.readonly) {
      event.preventDefault();
      return;
    }
    if (draggingNodeId === null) {
      const types = event.dataTransfer?.types;
      if (types && Array.from(types).includes('Files')) {
        const row = event.target.closest('.node');
        if (!row) {
          return;
        }
        event.preventDefault();
        clearDragOverStyles();
        row.classList.add('drag-over-inside');
        event.dataTransfer.dropEffect = 'copy';
      }
      return;
    }
    const row = event.target.closest('.node');
    if (!row) {
      return;
    }
    const targetId = Number(row.dataset.id);

    if (targetId === draggingNodeId || isDescendant(draggingNodeId, targetId)) {
      return;
    }

    event.preventDefault();

    const rect = row.getBoundingClientRect();
    const relativeY = event.clientY - rect.top;
    const height = rect.height;

    clearDragOverStyles();

    if (relativeY < height * 0.3) {
      row.classList.add('drag-over-above');
      event.dataTransfer.dropEffect = 'move';
    } else if (relativeY > height * 0.7) {
      row.classList.add('drag-over-below');
      event.dataTransfer.dropEffect = 'move';
    } else {
      row.classList.add('drag-over-inside');
      event.dataTransfer.dropEffect = 'move';
    }
  });

  outlineEl.addEventListener('dragleave', (event) => {
    const row = event.target.closest('.node');
    if (row) {
      row.classList.remove('drag-over-above', 'drag-over-below', 'drag-over-inside');
    }
  });

  outlineEl.addEventListener('drop', async (event) => {
    if (state.readonly) {
      event.preventDefault();
      return;
    }
    if (draggingNodeId === null) {
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        const row = event.target.closest('.node');
        if (!row) {
          return;
        }
        const targetId = Number(row.dataset.id);
        const targetNode = state.nodes.get(targetId);
        if (!targetNode) {
          return;
        }
        event.preventDefault();
        clearDragOverStyles();
        await uploadFilesToNode({ state, t, setStatus, apiUpload, flushNodeText, renderTree }, targetId, files);
      }
      return;
    }
    const row = event.target.closest('.node');
    if (!row) {
      return;
    }
    const targetId = Number(row.dataset.id);

    if (targetId === draggingNodeId || isDescendant(draggingNodeId, targetId)) {
      return;
    }

    event.preventDefault();

    const rect = row.getBoundingClientRect();
    const relativeY = event.clientY - rect.top;
    const height = rect.height;

    let newParentId = null;
    let newPosition = 0;

    const draggingNode = state.nodes.get(draggingNodeId);
    const targetNode = state.nodes.get(targetId);
    if (!draggingNode || !targetNode) {
      return;
    }

    const oldParentId = draggingNode.parent_id;

    if (relativeY < height * 0.3) {
      newParentId = targetNode.parent_id;
      const siblings = getChildren(newParentId);
      let targetIndex = siblings.indexOf(targetId);
      if (newParentId === oldParentId) {
        const draggingIndex = siblings.indexOf(draggingNodeId);
        if (draggingIndex !== -1 && draggingIndex < targetIndex) {
          targetIndex--;
        }
      }
      newPosition = targetIndex;
    } else if (relativeY > height * 0.7) {
      newParentId = targetNode.parent_id;
      const siblings = getChildren(newParentId);
      let targetIndex = siblings.indexOf(targetId);
      if (newParentId === oldParentId) {
        const draggingIndex = siblings.indexOf(draggingNodeId);
        if (draggingIndex !== -1 && draggingIndex < targetIndex) {
          targetIndex--;
        }
      }
      newPosition = targetIndex + 1;
    } else {
      newParentId = targetId;
      const children = getChildren(newParentId);
      newPosition = children.length;
    }

    clearDragOverStyles();
    await moveNode(draggingNodeId, newParentId, newPosition);
  });
}

export async function uploadFilesToNode(
  { state, t, setStatus, apiUpload, flushNodeText, renderTree },
  targetId,
  files
) {
  setStatus(t('status_saving'), 'saving');
  try {
    const node = state.nodes.get(targetId);
    if (!node) return;
    let text = node.text;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const res = await apiUpload('api/file_upload.php', file);
      if (res && res.url) {
        const isImage = res.mime_type.startsWith('image/');
        const label = escapeMarkdownLinkLabel(res.filename || 'file');
        const mdLink = isImage ? `![${label}](${res.url})` : `[${label}](${res.url})`;
        text = text ? `${text} ${mdLink}` : mdLink;
      }
    }

    await flushNodeText(targetId, text);
    renderTree();
    setStatus(t('status_saved'), 'saved');
  } catch (err) {
    console.error(err);
    setStatus(err instanceof Error ? err.message : 'Upload failed', 'error');
  }
}

export function escapeMarkdownLinkLabel(label) {
  return String(label || 'file').replace(/([\\\[\]])/g, '\\$1');
}
